import { UTCDate } from '@date-fns/utc';
import { format as dateFnsFormat, format, isBefore, isDate, isValid, parse as parseDate } from 'date-fns';
import { nl } from 'date-fns/locale';
import type { SortItem } from 'vuetify/lib/components/VDataTable/composables/sort.mjs';

export const maximumDate = formatDateFieldDate(new Date('9999-01-01'));
export const minimumDate = formatDateFieldDate(new Date('1900-01-01'));

/**
 * Returns the current month name in Dutch (e.g. "Januari")
 */
export function getCurrentMonthName(): string {
    return format(new Date(), 'LLLL', { locale: nl });
}

const monthNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;

type MonthNumber = (typeof monthNumbers)[number];
/**
 * @param number Month number (1 = January, 12 = December)
 * @returns Month name
 */
export function getMonthName(number: MonthNumber): string {
    const date = new UTCDate();
    date.setDate(1);
    date.setMonth(number - 1);
    return format(date, 'LLLL', { locale: nl });
}

export function getMonthNames() {
    return monthNumbers.map(getMonthName);
}

/**
 * @returns the current year - e.g 2024
 */
export const currentYear: number = new Date().getFullYear();

/** Takes a date object and converts it into a local date or time string in the format required for HTML5 date fields */
export function formatDateFieldDate(date?: Date, isDate: boolean = true) {
    const TIME_FIELD_FORMAT = 'HH:mm';

    if (!date) return '';
    const format = isDate ? getDateFieldFormat() : TIME_FIELD_FORMAT;
    return dateFnsFormat(date, format);
}

export function formatUserDate(date?: Date) {
    if (!date) return '';
    return dateFnsFormat(date, 'dd-MM-yyyy');
}

function getDateFieldFormat() {
    return 'yyyy-MM-dd';
}

/** Takes a local date string in the format required for HTML5 date fields and converts it back to a UTC date */
export function convertDateFieldDateToUTCDate(date: string, includeTime: boolean = false) {
    const DATETIME_FIELD_FORMAT = "yyyy-MM-dd'T'HH:mm";

    const format = includeTime ? DATETIME_FIELD_FORMAT : getDateFieldFormat();
    const utcDate = parseDate(date, format, new Date());
    return utcDate;
}

export function isISODate(data: any): boolean {
    const regex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/;
    return typeof data === 'string' && regex.test(data);
}

export function dateSort(a: Date | null, b: Date | null, order: SortItem['order']): number {
    if (!a) return order === 'asc' ? 1 : -1;
    if (!b) return order === 'asc' ? -1 : 1;

    return a.getTime() - b.getTime();
}

export function isValidDate(date: any) {
    return isDate(date) && isValid(date);
}

/**
 * @returns formatted date 18-01-2025 with time 12:34 if includeTime = true OR '-' if no correct date format has been provided
 */
export function toDisplayDate(date?: Date | null, includeTime = false): string {
    if (!date || !isValidDate(date)) return '-';

    const format = 'dd-MM-yyyy'.concat(includeTime ? ' HH:mm' : '');
    return dateFnsFormat(date, format);
}

/** Checks if a date is before the given before date, if no before date is given then defaults to the current datetime */
export function isDateBefore(date?: Date, beforeDate: Date = new Date()) {
    if (!date) return false;
    return isBefore(date, beforeDate);
}

/**
 * @param currentDate
 * @returns a Date object with the first day of the next month
 */
export function getNextMonthStartDate(date: Date): Date {
    const dateMonthLater = date.setMonth(date.getMonth() + 1);
    const newDate = new Date(dateMonthLater);
    return new Date(newDate.getFullYear(), newDate.getMonth(), 1);
}

/**
 * @param currentDate
 * @returns a Date object with the first day of the current month
 */
export function getCurrentMonthStartDate(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1);
}
